import { distance, type Point } from '../core/math';
import { AudioManager } from '../audio/audio-manager';
import type { Faction, GameMode } from '../data/types';
import { Battlefield } from '../rendering/battlefield';
import { GameSimulation } from '../simulation/game';
import { Panels, type PanelMetrics } from '../ui/panels';
import { NavigationClient } from '../workers/navigation-client';

interface KeyBindings {
  panUp: string;
  panDown: string;
  panLeft: string;
  panRight: string;
  attackMove: string;
  stop: string;
  hold: string;
  reverse: string;
  retreat: string;
  centerHq: string;
}
interface Settings {
  faction: Faction;
  mode: GameMode;
  difficulty: 'easy' | 'normal' | 'hard';
  seed: number;
  uiScale: number;
  reducedEffects: boolean;
  colorblindPalette: boolean;
  screenShake: boolean;
  edgeScrolling: boolean;
  scrollSpeed: number;
  volume: number;
  keyBindings: KeyBindings;
}
const defaultKeyBindings: KeyBindings = {
  panUp: 'w',
  panDown: 's',
  panLeft: 'a',
  panRight: 'd',
  attackMove: 'a',
  stop: 's',
  hold: 'h',
  reverse: 'v',
  retreat: 't',
  centerHq: 'Home',
};
const defaultSettings: Settings = {
  faction: 'equestria',
  mode: 'sector_control',
  difficulty: 'normal',
  seed: 1337,
  uiScale: 1,
  reducedEffects: false,
  colorblindPalette: false,
  screenShake: true,
  edgeScrolling: true,
  scrollSpeed: 220,
  volume: 0.35,
  keyBindings: defaultKeyBindings,
};
const normalizeKey = (key: string): string => {
  if (key === ' ') return 'Space';
  return key.length === 1 ? key.toLowerCase() : key;
};
const displayKey = (key: string): string => (key.length === 1 ? key.toUpperCase() : key);
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;
const readKeyBindings = (value: unknown): KeyBindings => {
  if (!isRecord(value)) return { ...defaultKeyBindings };
  return Object.fromEntries(
    Object.entries(defaultKeyBindings).map(([binding, fallback]) => {
      const candidate = value[binding];
      return [
        binding,
        typeof candidate === 'string' && candidate.length > 0 && candidate.length <= 20
          ? normalizeKey(candidate)
          : fallback,
      ];
    }),
  ) as KeyBindings;
};
const readSettings = (): Settings => {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem('hyperwar-settings-v1') ?? '{}');
    if (isRecord(raw)) {
      return {
        ...defaultSettings,
        ...raw,
        keyBindings: readKeyBindings(raw.keyBindings),
      } as Settings;
    }
  } catch {
    /* invalid local settings use defaults */
  }
  return defaultSettings;
};

export class AppController {
  private settings = readSettings();
  private simulation: GameSimulation | undefined;
  private battlefield: Battlefield | undefined;
  private panels: Panels | undefined;
  private frame: number | undefined;
  private previous = 0;
  private accumulator = 0;
  private paused = false;
  private confirmingAbandon = false;
  private placement: string | undefined;
  private targeting: { kind: 'ability' | 'support' | 'rally'; id: string } | undefined;
  private readonly audio = new AudioManager();
  private navigation: NavigationClient | undefined;
  private moveMode: 'move' | 'attack_move' | 'reverse' | 'face' | 'ground_attack' = 'move';
  private metrics: PanelMetrics = { fps: 0, tickMs: 0, frameMs: 0, pathJobs: 0, debug: false };
  private controlGroups: number[][] = Array.from({ length: 9 }, () => []);
  private readonly lastGroupPress = new Map<number, number>();
  private lastSelectionClick: { definitionId: string; time: number } | undefined;
  private subgroupPool: number[] = [];
  private subgroupIndex = 0;

  public constructor(private readonly root: HTMLElement) {}
  public start(): void {
    this.audio.setMaster(this.settings.volume);
    this.showMenu();
  }
  private showMenu(): void {
    this.stopMatch();
    this.root.innerHTML = `<div class="menu-shell"><section class="hero"><p class="eyebrow">TACTICAL 1V1 SKIRMISH</p><h1>HYPERWAR <span>RTS</span></h1><p>Lead Equestria or INTERDEF through the Kharkiv Approaches. Capture connected Supply Depots, establish command infrastructure, and request reinforcements.</p><button class="primary" data-menu="skirmish">Start Skirmish</button><button data-menu="settings">Settings</button><p class="hint">Primitive top-down tactical warfare · Single-player · v0.1.0</p></section></div>`;
    this.root
      .querySelector('[data-menu="skirmish"]')
      ?.addEventListener('click', () => this.showSetup());
    this.root
      .querySelector('[data-menu="settings"]')
      ?.addEventListener('click', () => this.showSettings());
  }
  private showSetup(): void {
    this.root.innerHTML = `<div class="menu-shell"><section class="setup"><p class="eyebrow">SKIRMISH SETUP</p><h1>Kharkiv Approaches</h1><fieldset class="map-card"><legend>Map</legend><label><input type="radio" name="map" checked /> <b>Kharkiv Approaches</b><small>One finished 1v1 battlefield: connected Supply Depots, three Victory Points, roads, industrial cover, and base ingress routes.</small></label></fieldset><label>Your faction<select id="faction"><option value="equestria">Equestria</option><option value="interdef">INTERDEF</option></select></label><p class="opponent">AI faction: <b id="ai-faction" aria-live="polite"></b></p><label>Mode<select id="mode"><option value="sector_control">Sector Control</option><option value="annihilation">Annihilation</option></select></label><label>AI difficulty<select id="difficulty"><option value="easy">Easy</option><option value="normal">Normal</option><option value="hard">Hard</option></select></label><label>Deterministic seed<input id="seed" type="number" value="${this.settings.seed}" /></label><p>Sector Control: hold Victory Points to drain 500 enemy tickets. Annihilation: destroy the enemy Field Headquarters.</p><button class="primary" id="start-match">Deploy</button><button id="back">Back</button></section></div>`;
    (this.root.querySelector('#faction') as HTMLSelectElement).value = this.settings.faction;
    (this.root.querySelector('#mode') as HTMLSelectElement).value = this.settings.mode;
    (this.root.querySelector('#difficulty') as HTMLSelectElement).value = this.settings.difficulty;
    const updateOpponent = (): void => {
      const faction = (this.root.querySelector('#faction') as HTMLSelectElement).value;
      const opponent = this.root.querySelector('#ai-faction');
      if (opponent) opponent.textContent = faction === 'equestria' ? 'INTERDEF' : 'Equestria';
    };
    updateOpponent();
    this.root.querySelector('#faction')?.addEventListener('change', updateOpponent);
    this.root.querySelector('#back')?.addEventListener('click', () => this.showMenu());
    this.root.querySelector('#start-match')?.addEventListener('click', () => {
      const faction = (this.root.querySelector('#faction') as HTMLSelectElement).value as Faction;
      const mode = (this.root.querySelector('#mode') as HTMLSelectElement).value as GameMode;
      const difficulty = (this.root.querySelector('#difficulty') as HTMLSelectElement)
        .value as Settings['difficulty'];
      const seed = Number((this.root.querySelector('#seed') as HTMLInputElement).value) || 1337;
      this.settings = { ...this.settings, faction, mode, difficulty, seed };
      this.saveSettings();
      void this.startMatch();
    });
  }
  private showSettings(): void {
    const keyInputs = Object.entries(this.settings.keyBindings)
      .map(
        ([binding, value]) =>
          `<label>${this.bindingLabel(binding as keyof KeyBindings)} <input class="key-binding" data-key-binding="${binding}" value="${displayKey(value)}" aria-label="${this.bindingLabel(binding as keyof KeyBindings)} key binding" readonly /></label>`,
      )
      .join('');
    this.root.innerHTML = `<div class="menu-shell"><section class="setup"><p class="eyebrow">SETTINGS</p><h1>Accessibility & presentation</h1><label>UI scale <input id="scale" type="range" min="0.8" max="1.35" step="0.05" value="${this.settings.uiScale}" /></label><label>Master volume <input id="volume" type="range" min="0" max="1" step="0.05" value="${this.settings.volume}" /></label><label class="check"><input id="effects" type="checkbox" ${this.settings.reducedEffects ? 'checked' : ''} /> Reduced effects</label><label class="check"><input id="colorblind" type="checkbox" ${this.settings.colorblindPalette ? 'checked' : ''} /> Colorblind-friendly ownership palette</label><label class="check"><input id="screen-shake" type="checkbox" ${this.settings.screenShake ? 'checked' : ''} /> Screen shake on strikes</label><label class="check"><input id="edge-scroll" type="checkbox" ${this.settings.edgeScrolling ? 'checked' : ''} /> Edge scrolling</label><label>Edge scroll speed <input id="scroll-speed" type="range" min="100" max="420" step="20" value="${this.settings.scrollSpeed}" /></label><fieldset class="key-bindings"><legend>Key bindings</legend><p>Focus a binding and press a key. Movement and command bindings share A/S contextually: pan with no selection, command with a selection.</p><div>${keyInputs}</div><button type="button" id="reset-bindings">Restore tactical defaults</button></fieldset><p>Mouse and keyboard menus use standard browser focus states. Active bindings: ${displayKey(this.settings.keyBindings.attackMove)} attack-move, ${displayKey(this.settings.keyBindings.stop)} stop, ${displayKey(this.settings.keyBindings.hold)} hold, ${displayKey(this.settings.keyBindings.reverse)} reverse, ${displayKey(this.settings.keyBindings.retreat)} retreat.</p><button class="primary" id="save">Save settings</button><button id="back">Back</button></section></div>`;
    this.bindKeyInputs();
    this.root.querySelector('#reset-bindings')?.addEventListener('click', () => {
      for (const input of this.root.querySelectorAll<HTMLInputElement>('[data-key-binding]')) {
        const binding = input.dataset.keyBinding as keyof KeyBindings;
        input.value = displayKey(defaultKeyBindings[binding]);
      }
    });
    this.root.querySelector('#save')?.addEventListener('click', () => {
      this.settings.uiScale = Number((this.root.querySelector('#scale') as HTMLInputElement).value);
      this.settings.reducedEffects = (
        this.root.querySelector('#effects') as HTMLInputElement
      ).checked;
      this.settings.colorblindPalette = (
        this.root.querySelector('#colorblind') as HTMLInputElement
      ).checked;
      this.settings.screenShake = (
        this.root.querySelector('#screen-shake') as HTMLInputElement
      ).checked;
      this.settings.edgeScrolling = (
        this.root.querySelector('#edge-scroll') as HTMLInputElement
      ).checked;
      this.settings.scrollSpeed = Number(
        (this.root.querySelector('#scroll-speed') as HTMLInputElement).value,
      );
      this.settings.volume = Number((this.root.querySelector('#volume') as HTMLInputElement).value);
      const keyBindings = { ...defaultKeyBindings };
      for (const input of this.root.querySelectorAll<HTMLInputElement>('[data-key-binding]')) {
        const binding = input.dataset.keyBinding as keyof KeyBindings;
        keyBindings[binding] = normalizeKey(input.value);
      }
      this.settings.keyBindings = readKeyBindings(keyBindings);
      this.audio.setMaster(this.settings.volume);
      this.saveSettings();
      this.showMenu();
    });
    this.root.querySelector('#back')?.addEventListener('click', () => this.showMenu());
  }
  private async startMatch(): Promise<void> {
    this.controlGroups = Array.from({ length: 9 }, () => []);
    this.lastGroupPress.clear();
    this.lastSelectionClick = undefined;
    this.subgroupPool = [];
    this.subgroupIndex = 0;
    this.simulation = new GameSimulation(
      this.settings.faction,
      this.settings.mode,
      this.settings.seed,
      this.settings.difficulty,
    );
    this.panels = new Panels(this.root, {
      request: (building, unit) =>
        this.notice(this.simulation?.requestReinforcement(building, unit)),
      cancelRequest: (building, request) => this.simulation?.cancelRequest(building, request),
      rally: (building) => {
        this.targeting = { kind: 'rally', id: String(building) };
        this.notice('Choose a rally point on the battlefield.');
      },
      build: (id) => {
        this.placement = id;
        this.notice('Select a location within your base construction zone.');
      },
      retreat: () => this.simulation?.retreat(this.selectedIds()),
      ability: (id) => {
        this.targeting = { kind: 'ability', id };
        this.notice('Choose an ability target on the battlefield.');
      },
      support: (id) => {
        this.targeting = { kind: 'support', id };
        this.notice('Choose a support target on the battlefield.');
      },
      exitGarrison: (building) => {
        const structure = this.simulation?.getEntity(building);
        if (structure)
          this.notice(
            this.simulation?.exitGarrison(building, {
              x: structure.position.x + 90,
              y: structure.position.y,
            }),
          );
      },
      selectPassenger: (id) => this.select([id]),
      minimap: (point) => this.battlefield?.centerOn(point),
      camera: () => this.battlefield?.cameraBounds(),
      colorblindPalette: () => this.settings.colorblindPalette,
      pause: () => this.togglePause(),
      menu: () => this.showMenu(),
    });
    const host = this.panels.initialize();
    this.battlefield = new Battlefield(host);
    this.navigation = new NavigationClient();
    await this.battlefield.initialize();
    this.battlefield.setColorblindPalette(this.settings.colorblindPalette);
    this.battlefield.setScreenShake(this.settings.screenShake);
    this.battlefield.setEdgeScrolling(this.settings.edgeScrolling, this.settings.scrollSpeed);
    this.battlefield.onClick = (point, shift) => this.click(point, shift);
    this.battlefield.onRightClick = (point) => this.rightClick(point);
    this.battlefield.onBox = (from, to, shift) => this.box(from, to, shift);
    window.addEventListener('keydown', this.onKeyDown);
    this.previous = performance.now();
    this.loop(this.previous);
  }
  private loop(now: number): void {
    const simulation = this.simulation;
    const battlefield = this.battlefield;
    const panels = this.panels;
    if (!simulation || !battlefield || !panels) return;
    const elapsed = Math.min(0.25, (now - this.previous) / 1000);
    this.previous = now;
    if (!this.paused) {
      this.accumulator += elapsed;
      let steps = 0;
      const tickStarted = performance.now();
      while (this.accumulator >= 0.05 && steps++ < 5) {
        simulation.tick();
        this.accumulator -= 0.05;
      }
      this.metrics.tickMs = performance.now() - tickStarted;
    }
    const snapshot = simulation.snapshot();
    battlefield.render(snapshot);
    this.metrics.fps = elapsed > 0 ? Math.round(1 / elapsed) : this.metrics.fps;
    this.metrics.frameMs = elapsed * 1000;
    this.metrics.pathJobs = this.navigation?.pendingJobs ?? 0;
    panels.draw(snapshot, this.settings.faction, this.metrics);
    if (simulation.winner) {
      this.showEnd(simulation.winner);
      return;
    }
    this.frame = requestAnimationFrame((time) => this.loop(time));
  }
  private click(point: Point, shift: boolean): void {
    const simulation = this.simulation;
    if (!simulation) return;
    if (this.placement) {
      const result = simulation.placeBuilding(this.placement, point);
      this.notice(result ?? 'Placement accepted. Engineers are constructing the command building.');
      if (!result) this.placement = undefined;
      return;
    }
    if (this.targeting) {
      const result =
        this.targeting.kind === 'support'
          ? simulation.activateSupport(this.targeting.id, point)
          : this.targeting.kind === 'rally'
            ? (simulation.setRally(Number(this.targeting.id), point), undefined)
            : simulation.activateAbility(this.selectedIds(), this.targeting.id, point);
      this.notice(result ?? 'Ability confirmed.');
      if (!result) this.targeting = undefined;
      return;
    }
    const nearest = simulation
      .allEntities()
      .filter((entity) => distance(entity.position, point) < (entity.kind === 'building' ? 65 : 35))
      .sort((a, b) => distance(a.position, point) - distance(b.position, point))[0];
    const old = this.selectedIds();
    const now = performance.now();
    const doubleClick =
      nearest &&
      this.lastSelectionClick?.definitionId === nearest.definitionId &&
      now - this.lastSelectionClick.time < 350;
    this.lastSelectionClick = nearest
      ? { definitionId: nearest.definitionId, time: now }
      : undefined;
    if (!nearest) this.select([]);
    else if (doubleClick)
      this.select(
        simulation
          .allEntities()
          .filter(
            (entity) =>
              entity.owner === this.settings.faction &&
              entity.definitionId === nearest.definitionId &&
              entity.visible !== false &&
              !entity.embarkedIn &&
              !entity.garrisonedIn,
          )
          .map((entity) => entity.id),
      );
    else if (shift)
      this.select(
        old.includes(nearest.id) ? old.filter((id) => id !== nearest.id) : [...old, nearest.id],
      );
    else this.select([nearest.id]);
  }
  private box(from: Point, to: Point, shift: boolean): void {
    const simulation = this.simulation;
    if (!simulation) return;
    const left = Math.min(from.x, to.x);
    const right = Math.max(from.x, to.x);
    const top = Math.min(from.y, to.y);
    const bottom = Math.max(from.y, to.y);
    const found = simulation
      .allEntities()
      .filter(
        (entity) =>
          entity.owner === this.settings.faction &&
          entity.position.x >= left &&
          entity.position.x <= right &&
          entity.position.y >= top &&
          entity.position.y <= bottom,
      )
      .map((entity) => entity.id);
    this.select(shift ? [...new Set([...this.selectedIds(), ...found])] : found);
  }
  private rightClick(point: Point): void {
    const simulation = this.simulation;
    if (!simulation) return;
    const friendlyTransport = simulation
      .allEntities()
      .filter(
        (entity) => entity.owner === this.settings.faction && (entity.transportSlots ?? 0) > 0,
      )
      .sort((a, b) => distance(a.position, point) - distance(b.position, point))[0];
    const selected = this.selectedIds();
    if (this.moveMode === 'face') {
      simulation.face(selected, point);
      this.moveMode = 'move';
      this.notice('Selected units are facing the chosen direction.');
      return;
    }
    if (this.moveMode === 'ground_attack') {
      const result = simulation.groundAttack(selected, point);
      if (!result) this.moveMode = 'move';
      this.notice(result ?? 'Ground attack confirmed.');
      return;
    }
    const selectedCommandBuilding = selected
      .map((id) => simulation.getEntity(id))
      .find(
        (entity) =>
          entity?.owner === this.settings.faction &&
          entity.kind === 'building' &&
          !entity.construction,
      );
    if (selectedCommandBuilding) {
      simulation.setRally(selectedCommandBuilding.id, point);
      this.notice(`${selectedCommandBuilding.name} rally point set.`);
      return;
    }
    if (
      friendlyTransport &&
      distance(friendlyTransport.position, point) < 50 &&
      selected.some((id) => id !== friendlyTransport.id)
    ) {
      this.notice(simulation.embark(selected, friendlyTransport.id));
      return;
    }
    const selectedTransport = selected
      .map((id) => simulation.getEntity(id))
      .find((entity) => entity && (entity.transportSlots ?? 0) > 0);
    if (selectedTransport?.passengers?.length) {
      this.notice(simulation.disembark(selectedTransport.id, point));
      return;
    }
    const friendlyVehicle = simulation
      .allEntities()
      .filter(
        (entity) =>
          entity.owner === this.settings.faction &&
          entity.category === 'vehicle' &&
          (entity.health < entity.maxHealth || entity.critical) &&
          distance(entity.position, point) < 45,
      )
      .sort((a, b) => distance(a.position, point) - distance(b.position, point))[0];
    if (friendlyVehicle) {
      const result = simulation.repair(selected, friendlyVehicle.id);
      this.notice(result);
      return;
    }
    const garrison = simulation
      .allEntities()
      .filter((entity) => entity.garrisonSlots && distance(entity.position, point) < 60)
      .sort((a, b) => distance(a.position, point) - distance(b.position, point))[0];
    if (garrison) {
      this.notice(simulation.garrison(selected, garrison.id));
      return;
    }
    const enemy = simulation
      .allEntities()
      .filter((entity) => entity.owner !== this.settings.faction && entity.owner !== 'neutral')
      .sort((a, b) => distance(a.position, point) - distance(b.position, point))[0];
    if (enemy && distance(enemy.position, point) < 45)
      simulation.issueAttack(this.selectedIds(), enemy.id);
    else this.issueMove(point);
  }
  private issueMove(point: Point): void {
    const simulation = this.simulation;
    if (!simulation) return;
    const ids = this.selectedIds();
    const attackMove = this.moveMode === 'attack_move';
    const reverse = this.moveMode === 'reverse';
    simulation.issueMove(ids, point, attackMove, reverse);
    this.moveMode = 'move';
    for (const id of ids) {
      const entity = simulation.getEntity(id);
      if (!entity || entity.air || entity.embarkedIn || entity.garrisonedIn) continue;
      this.navigation?.request(
        id,
        entity.position,
        point,
        entity.category === 'infantry'
          ? 'infantry'
          : entity.definitionId.includes('wc') ||
              entity.definitionId.includes('m113') ||
              entity.definitionId.includes('fennek')
            ? 'wheeled'
            : 'tracked',
        (waypoints) => simulation.applyPath(id, point, waypoints),
      );
    }
  }
  private selectedIds(): number[] {
    return (
      this.simulation
        ?.allEntities()
        .filter((entity) => entity.selected)
        .map((entity) => entity.id) ?? []
    );
  }
  private select(ids: number[]): void {
    this.simulation?.select(ids);
    this.subgroupPool = [...ids];
    this.subgroupIndex = 0;
  }
  private togglePause(): void {
    this.paused = !this.paused;
    if (!this.paused) this.confirmingAbandon = false;
    this.panels?.setPaused(this.paused);
    this.renderPauseModal();
  }
  private renderPauseModal(): void {
    const modal = this.root.querySelector<HTMLElement>('#modal-layer');
    if (!modal) return;
    if (!this.paused) {
      modal.innerHTML = '';
      return;
    }
    modal.innerHTML = this.confirmingAbandon
      ? '<section class="pause-card" role="dialog" aria-modal="true" aria-label="Abandon match confirmation"><p class="eyebrow">CONFIRM</p><h2>Abandon this match?</h2><p>Your current skirmish will be lost.</p><button id="cancel-abandon">Keep playing</button><button id="confirm-abandon">Abandon match</button></section>'
      : '<section class="pause-card" role="dialog" aria-modal="true" aria-label="Pause menu"><p class="eyebrow">PAUSED</p><h2>Match paused</h2><button id="resume">Resume</button><button id="abandon">Return to menu</button></section>';
    modal.querySelector('#resume')?.addEventListener('click', () => this.togglePause());
    modal.querySelector('#abandon')?.addEventListener('click', () => {
      this.confirmingAbandon = true;
      this.renderPauseModal();
    });
    modal.querySelector('#cancel-abandon')?.addEventListener('click', () => {
      this.confirmingAbandon = false;
      this.renderPauseModal();
    });
    modal.querySelector('#confirm-abandon')?.addEventListener('click', () => this.showMenu());
  }
  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (!this.simulation || event.repeat) return;
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement)
      return;
    const groupNumber = Number(event.key);
    if (Number.isInteger(groupNumber) && groupNumber >= 1 && groupNumber <= 9) {
      event.preventDefault();
      const groupIndex = groupNumber - 1;
      if (event.ctrlKey) {
        this.controlGroups[groupIndex] = this.selectedIds().filter(
          (id) => this.simulation?.getEntity(id)?.owner === this.settings.faction,
        );
        this.lastGroupPress.delete(groupIndex);
        return;
      }
      const ids = (this.controlGroups[groupIndex] ?? []).filter((id) => {
        const entity = this.simulation?.getEntity(id);
        return entity?.owner === this.settings.faction && !entity.embarkedIn;
      });
      if (!ids.length) return;
      const now = performance.now();
      if (now - (this.lastGroupPress.get(groupIndex) ?? -Infinity) < 350) {
        const members = ids
          .map((id) => this.simulation?.getEntity(id))
          .filter((entity): entity is NonNullable<typeof entity> => Boolean(entity));
        const center = members.reduce(
          (sum, entity) => ({ x: sum.x + entity.position.x, y: sum.y + entity.position.y }),
          { x: 0, y: 0 },
        );
        if (members.length)
          this.battlefield?.centerOn({
            x: center.x / members.length,
            y: center.y / members.length,
          });
      }
      this.lastGroupPress.set(groupIndex, now);
      this.select(ids);
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      if (this.placement || this.targeting || this.moveMode !== 'move') {
        this.placement = undefined;
        this.targeting = undefined;
        this.moveMode = 'move';
      } else this.togglePause();
      return;
    }
    if (event.key === 'F3') {
      event.preventDefault();
      this.metrics.debug = !this.metrics.debug;
      this.panels?.invalidate();
      return;
    }
    if (event.key === 'Tab') {
      event.preventDefault();
      const pool = this.subgroupPool.length ? this.subgroupPool : this.selectedIds();
      const members = pool
        .map((id) => this.simulation?.getEntity(id))
        .filter((entity): entity is NonNullable<typeof entity> => Boolean(entity));
      const types = [...new Set(members.map((entity) => entity.definitionId))];
      if (types.length > 1) {
        this.subgroupIndex = (this.subgroupIndex + 1) % types.length;
        const type = types[this.subgroupIndex];
        if (type)
          this.simulation.select(
            members.filter((entity) => entity.definitionId === type).map((entity) => entity.id),
          );
      }
      return;
    }
    const key = normalizeKey(event.key);
    const bindings = this.settings.keyBindings;
    const selected = this.selectedIds();
    const bindingUsed = Object.values(bindings).includes(key);
    if (bindingUsed) event.preventDefault();
    if (key === bindings.panUp) this.battlefield?.panBy(0, -180);
    if (key === bindings.panRight) this.battlefield?.panBy(180, 0);
    if (key === bindings.panLeft && !selected.length) this.battlefield?.panBy(-180, 0);
    if (key === bindings.panDown && !selected.length) this.battlefield?.panBy(0, 180);
    if (key === bindings.retreat) this.simulation.retreat(selected);
    if (key === bindings.attackMove && selected.length) {
      this.moveMode = 'attack_move';
      this.notice('Attack-move: right-click a destination.');
    }
    if (key === bindings.reverse) {
      this.moveMode = 'reverse';
      this.notice('Reverse: right-click a destination while a vehicle is selected.');
    }
    if (key === 'f') {
      this.moveMode = 'face';
      this.notice('Face direction: right-click a point.');
    }
    if (key === 'g') {
      this.moveMode = 'ground_attack';
      this.notice('Ground attack: right-click a target point.');
    }
    if (key === bindings.stop && selected.length) this.simulation.stop(selected);
    if (key === bindings.hold) this.simulation.hold(selected);
    if (key === bindings.centerHq) {
      const hq = this.simulation
        .allEntities()
        .find(
          (entity) =>
            entity.owner === this.settings.faction &&
            entity.definitionId === (this.settings.faction === 'equestria' ? 'eq_hq' : 'id_hq'),
        );
      if (hq) this.battlefield?.centerOn(hq.position);
    }
  };
  private notice(text: string | undefined): void {
    if (text) {
      this.audio.cue('ui');
      this.panels?.notify(text);
    }
  }
  private showEnd(winner: Faction): void {
    const sim = this.simulation;
    const elapsed = sim ? Math.floor(sim.time) : 0;
    const mode = sim?.mode;
    const elapsedFmt = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, '0')}`;
    const reason =
      mode === 'annihilation'
        ? `${winner === 'equestria' ? "Equestria's" : "INTERDEF's"} headquarters was destroyed.`
        : `Ticket advantage secured ${winner === 'equestria' ? 'Equestrian' : 'INTERDEF'} control.`;
    const unitsFielded = sim
      ? sim.allEntities().filter((entity) => entity.owner === winner && entity.kind === 'unit')
          .length
      : 0;
    const supply = sim ? Math.floor(sim.snapshot().players[winner].supply) : 0;
    this.stopMatch();
    const victory = winner === this.settings.faction;
    this.root.innerHTML = `<div class="menu-shell"><section class="hero"><p class="eyebrow">MATCH COMPLETE</p><h1>${victory ? 'VICTORY' : 'DEFEAT'}</h1><p>${reason}</p><ul class="end-stats"><li>Match time: ${elapsedFmt}</li><li>Units fielded: ${unitsFielded}</li><li>Supply remaining: ${supply}</li></ul><button class="primary" id="menu">Return to menu</button></section></div>`;
    this.root.querySelector('#menu')?.addEventListener('click', () => this.showMenu());
  }
  private stopMatch(): void {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = undefined;
    this.battlefield?.app.destroy(true);
    this.navigation?.dispose();
    this.navigation = undefined;
    window.removeEventListener('keydown', this.onKeyDown);
    this.battlefield = undefined;
    this.panels = undefined;
    this.simulation = undefined;
    this.paused = false;
    this.confirmingAbandon = false;
  }
  private saveSettings(): void {
    localStorage.setItem('hyperwar-settings-v1', JSON.stringify(this.settings));
    document.documentElement.style.setProperty('--ui-scale', String(this.settings.uiScale));
  }
  private bindKeyInputs(): void {
    for (const input of this.root.querySelectorAll<HTMLInputElement>('[data-key-binding]')) {
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Tab') return;
        event.preventDefault();
        if (['Control', 'Shift', 'Alt', 'Meta'].includes(event.key)) return;
        input.value = displayKey(normalizeKey(event.key));
      });
    }
  }
  private bindingLabel(binding: keyof KeyBindings): string {
    return (
      {
        panUp: 'Pan up',
        panDown: 'Pan down',
        panLeft: 'Pan left',
        panRight: 'Pan right',
        attackMove: 'Attack-move',
        stop: 'Stop',
        hold: 'Hold position',
        reverse: 'Reverse vehicle',
        retreat: 'Retreat infantry',
        centerHq: 'Center headquarters',
      } satisfies Record<keyof KeyBindings, string>
    )[binding];
  }
}
